"use client";

import Image from "next/image";
import styles from "./navbar.module.css";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { Link } from "@/i18n/navigation";
import { useTranslations, useLocale } from "next-intl";
import {
  KeyboardArrowDown,
  Language,
  Person,
  Menu,
  Dashboard,
  Logout,
} from "@mui/icons-material";
import { useAuthDialogsContext } from "@/components/auth-dialog/auth-dialog-context";
import { useState, useRef, useEffect } from "react";
import MobileDrawer from "./mobile-drawer"; // IMPORT THE DRAWER
import { useDomainContext } from "@/global-context/domain-context";
import {
  getLocaleDataFromDomain,
  getLocaleTarget,
} from "@/lib/locale-data/locale";
import { useSettingsContext } from "@/components/navbar/settings-component/settings-context";
import { CircleFlag } from "react-circle-flags";
import SettingsDialog from "@/components/navbar/settings-component/settings-dialog";
import { handleSignOutAction } from "@/app/[locale]/(auth)/login/actions";
import { useExperienceContext } from "@/global-context/experience-context";

export default function NavbarV2() {
  const { userData } = useUserAuthDataContext();
  const { showSignIn } = useAuthDialogsContext();
  const t = useTranslations("FullMenu");
  const locale = useLocale();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false); // DRAWER STATE
  const profileRef = useRef<HTMLDivElement>(null);
  const domain = useDomainContext();
  const localeData = getLocaleTarget({ domain, locale });
  const { isSettingsOpen, setIsSettingsOpen } = useSettingsContext();
  const { homeHref } = useExperienceContext();

  const isAuthenticated = !!userData?.sessionId;

  const executeSignOut = async () => {
    setIsProfileOpen(false);
    // Grab the current raw path from the browser to redirect back here!
    const currentPath = window.location.pathname + window.location.search;
    await handleSignOutAction({ currentPath });
  };

  const handleSignIn = () => {
    const host = window.location.host; // e.g., 'doomum.de:3000'
    const protocol = window.location.protocol;
    const currentUrl = `${protocol}//${host}${window.location.pathname}${window.location.search}`;

    const isLocalDev =
      window.location.port === "3000" || host.includes("localhost");

    const authHub = isLocalDev
      ? "https://dev-id.doomum.com:3000"
      : "https://id.doomum.com";

    // The /sso route logic handles the "Is there a cookie?" check for us.
    const ssoUrl = `${authHub}/sso?destination=${encodeURIComponent(currentUrl)}`;

    window.location.href = ssoUrl;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className={styles.header}>
      <nav className={styles.nav}>
        <div className={styles.leftSection}>
          {/* 1. Logo Zone */}
          {/* <Link href="/" className={styles.logoLink}>
           
            <Image
              className={styles.logo}
              src="/logo.png"
              alt="Doomum"
              width={133}
              height={35}
              priority
            />
          </Link> */}
          <Link
            href={homeHref}
            className={styles.logoLink}
            aria-label="Doomum Home"
          >
            <h2 className={styles.logoText}>doomum</h2>
          </Link>

          {/* 2. Main Navigation (Desktop ONLY > 1280px) */}
          <div className={styles.centerLinks}>
            <Link href="/buy" className={styles.navLink}>
              Buy
            </Link>
            <Link href="/rent" className={styles.navLink}>
              Rent
            </Link>
            <Link href="/new-developments" className={styles.navLink}>
              New Developments
            </Link>
            <Link href="/invest" className={styles.navLink}>
              Invest
            </Link>
            <Link href="/commercial" className={styles.navLink}>
              Commercial
            </Link>
            <Link href="/agents" className={styles.navLink}>
              Find Agents
            </Link>
            <button className={styles.dropdownTrigger}>
              <span>Resources</span>
              <KeyboardArrowDown className={styles.chevron} />
            </button>
          </div>
        </div>

        {/* 3. Actions Zone */}
        <div className={styles.actions}>
          {/* Global / Settings Selector (Desktop) */}
          <button
            className={styles.globalBtn}
            onClick={() => setIsSettingsOpen(true)}
          >
            {localeData.region === "GLOBAL" ? (
              <Language className={styles.iconSm} />
            ) : (
              <CircleFlag
                countryCode={localeData.region.toLowerCase()}
                className={styles.iconSm}
              />
            )}
            <span className={styles.globalText}>
              {localeData.region === "GLOBAL" ? "Global" : localeData.name} |{" "}
              {userData.currency}
            </span>
          </button>

          {/* Auth State (Desktop) */}
          {isAuthenticated ? (
            <div className={styles.profileWrapper} ref={profileRef}>
              <button
                className={styles.signInBtn}
                onClick={() => setIsProfileOpen(!isProfileOpen)}
              >
                {userData.user_photo ? (
                  <Image
                    src={userData.user_photo}
                    alt="Profile"
                    width={20}
                    height={20}
                    className={styles.avatar}
                  />
                ) : (
                  <Person className={styles.iconSm} />
                )}
                <span className={styles.truncate}>
                  {userData.complete_name}
                </span>
                <KeyboardArrowDown className={styles.chevron} />
              </button>

              {isProfileOpen && (
                <div className={styles.dropdownMenu}>
                  <Link
                    href="/dashboard"
                    className={styles.dropdownItem}
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <Dashboard className={styles.iconSm} />
                    <span>Dashboard</span>
                  </Link>
                  <Link
                    href="/my-account"
                    className={styles.dropdownItem}
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <Person className={styles.iconSm} />
                    <span>My Account</span>
                  </Link>

                  <hr className={styles.dropdownDivider} />

                  <button
                    onClick={executeSignOut}
                    className={styles.signOutItem}
                  >
                    <Logout className={styles.iconSm} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className={styles.signInBtn} onClick={handleSignIn}>
              {t("signIn")}
            </button>
          )}

          {/* Primary CTA (Desktop) */}
          <Link href="/new-listing" className={styles.listPropertyBtn}>
            List Property
          </Link>

          {/* Hamburger Menu (Mobile/Tablet) */}
          <button
            className={styles.hamburgerBtn}
            onClick={() => setIsMobileDrawerOpen(true)}
          >
            <Menu />
          </button>
        </div>
      </nav>

      {/* Render the Drawer */}
      <MobileDrawer
        isOpen={isMobileDrawerOpen}
        onClose={() => setIsMobileDrawerOpen(false)}
        onOpenSettings={() => setIsSettingsOpen(!isSettingsOpen)}
        isAuthenticated={isAuthenticated}
        onSignOut={executeSignOut}
        onSignIn={handleSignIn}
        locale={locale}
      />
      <SettingsDialog
        isSettingsOpen={isSettingsOpen}
        closeDialog={() => setIsSettingsOpen(false)}
      />
    </header>
  );
}
